import React, { useEffect, useMemo, useRef, useState } from 'react';
import cv from './cv.json';
import './CvTimeline.css';

// Какие разделы CV попадают на таймлайн и как подписываются
const KINDS = {
  experience: 'Work',
  achievements: 'Award',
  projects: 'Project',
  education: 'Education',
};

const formatDate = iso =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// Год для группировки — последний год в диапазоне ("2025 – 2026" -> 2026),
// но не позже текущего: то, что ещё идёт, показываем в этом году
const endYear = date =>
  Math.min(Math.max(...(date.match(/\d{4}/g) || ['0']).map(Number)), new Date().getFullYear());

// Плавное появление элемента при прокрутке
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      el.classList.add('is-visible');
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Event({ e }) {
  const ref = useReveal();
  return (
    <li ref={ref} className="tl-event">
      <span className="tl-dot" aria-hidden="true" />
      <div className="tl-event-head">
        <span className="tl-kind">{KINDS[e.kind]}</span>
        <span className="tl-range">{e.date}</span>
      </div>
      <h3 className="tl-title">
        {e.url ? (
          <a href={e.url} target="_blank" rel="noopener noreferrer">
            {e.title} <span aria-hidden="true">↗</span>
          </a>
        ) : (
          e.title
        )}
      </h3>
      {e.subtitle && <p className="tl-sub">{e.subtitle}</p>}
      {e.place && <p className="tl-place">{e.place}</p>}
      {e.items.length > 0 && (
        <ul className="tl-items">
          {e.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function CvTimeline() {
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${cv.name} — CV`;
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const summary = cv.sections.find(s => s.type === 'text');
  const skills = cv.sections.find(s => s.type === 'skills');

  // Все события из разделов CV, сгруппированные по годам (новые сверху)
  const years = useMemo(() => {
    const events = cv.sections
      .filter(s => KINDS[s.id])
      .flatMap(s => s.entries.map(e => ({ ...e, kind: s.id })))
      .filter(e => filter === 'all' || e.kind === filter);
    const groups = {};
    events.forEach(e => {
      const y = endYear(e.date);
      (groups[y] = groups[y] || []).push(e);
    });
    return Object.keys(groups)
      .sort((a, b) => b - a)
      .map(y => ({ year: y, events: groups[y] }));
  }, [filter]);

  return (
    <div className="tl-page">
      <header className="tl-header">
        <a href="/" className="tl-home">
          ← levandox
        </a>
        <span className="tl-updated">
          updated <time dateTime={cv.lastUpdated}>{formatDate(cv.lastUpdated)}</time>
        </span>
      </header>

      <main className="tl-main">
        <section className="tl-hero">
          <div>
            <h1 className="tl-name">{cv.name}</h1>
            <p className="tl-role">
              {cv.title} · {cv.location}
            </p>
            <ul className="tl-links">
              {cv.links.map(l => (
                <li key={l.url}>
                  <a
                    href={l.url}
                    {...(l.url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              {cv.pdf && (
                <li>
                  <a href={cv.pdf} download className="tl-pdf">
                    ↓ PDF
                  </a>
                </li>
              )}
            </ul>
          </div>
          {cv.photo && <img className="tl-photo" src={cv.photo} alt={cv.name} width="128" height="128" />}
        </section>

        {summary && <p className="tl-summary">{summary.text}</p>}

        <nav className="tl-filters" aria-label="Filter timeline">
          {['all', ...Object.keys(KINDS)].map(k => (
            <button
              key={k}
              type="button"
              className="tl-filter"
              aria-pressed={filter === k}
              onClick={() => setFilter(k)}
            >
              {k === 'all' ? 'All' : KINDS[k]}
            </button>
          ))}
        </nav>

        <div className="tl-line">
          {years.map(({ year, events }) => (
            <section key={year} className="tl-year">
              <h2 className="tl-year-label">{year}</h2>
              <ol className="tl-events">
                {events.map((e, i) => (
                  <Event key={`${filter}-${year}-${i}`} e={e} />
                ))}
              </ol>
            </section>
          ))}
        </div>

        {skills && (
          <section className="tl-skills">
            <h2 className="tl-skills-title">Stack</h2>
            <dl>
              {skills.skills.map(s => (
                <div key={s.label} className="tl-skill">
                  <dt>{s.label}</dt>
                  <dd>{s.values.join(' · ')}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
      </main>
    </div>
  );
}
